import React, { useState } from 'react';
import './Board.scss';
import { formatTokenAmount } from '../../utils/formatTokenAmount';

const getConstant = (gameData, key, defaultValue) => {
  return gameData?.constants?.[key] ?? defaultValue;
};

const Board = ({ onClose, gameData }) => {
  const [activeSection, setActiveSection] = useState('gameInfo');

  const dailyTokenPool = getConstant(gameData, 'DAILY_TOKEN_POOL', 40);
  const rewardTokenSymbol = getConstant(gameData, 'REWARD_TOKEN_SYMBOL', 'DCKSLAP');
  const rewardTokenDisplay = getConstant(gameData, 'REWARD_TOKEN_DISPLAY', '$DCKSLAP');
  const intervalsPerDay = getConstant(gameData, 'MINING_INTERVALS_PER_DAY', 288);
  const intervalMinutes = getConstant(gameData, 'MINING_INTERVAL_MINUTES', 5);
  const startingBalance = getConstant(gameData, 'STARTING_BALANCE', 10);
  const rigTierCosts = getConstant(gameData, 'RIG_TIER_COSTS', [10, 20, 40, 80]);
  const minWalletBalance = getConstant(gameData, 'MIN_WALLET_BALANCE_FOR_MINING', 15000);
  const miningRequirementTokenSymbol = getConstant(
    gameData,
    'MINING_REQUIREMENT_TOKEN_SYMBOL',
    '$REDDICKS'
  );

  const sections = {
    gameInfo: {
      title: 'What is Reddicks Game?',
      content: (
        <>
          <p>Step into a fully-realized 3D cyberpunk world where your entrepreneurial dreams come to life. Reddicks Game leverages WebGL for a browser-based 3D experience without downloads.</p>
          <p>Purchase mining rigs in your virtual office, qualify your wallet through the REDDICKS holder gate, and earn {rewardTokenDisplay} as the scheduled payout job runs in the background.</p>
          <p>This is an immersive tycoon experience that bridges traditional gaming and Web3 play-to-earn mechanics on the Radix network.</p>
          <p>The game is still evolving, with plans for multiplayer features, NFT integration, and a deeper virtual economy.</p>
        </>
      )
    },
    controls: {
      title: 'Controls',
      content: (
        <>
          <p>Navigate the office using your keyboard and mouse:</p>
          <ul>
            <li><strong>W/A/S/D (or Z/Q/S/D):</strong> Move Forward/Left/Backward/Right</li>
            <li><strong>Shift:</strong> Hold to Run</li>
            <li><strong>Mouse Movement:</strong> Look Around (requires cursor lock)</li>
            <li><strong>Click on Game Window:</strong> Lock Cursor (enables looking around)</li>
            <li><strong>Middle Mouse Button:</strong> Toggle Cursor Lock (allows interaction with menus/UI)</li>
            <li><strong>Click on Buttons (e.g., 'Board', 'Mining Rigs'):</strong> Open Menus (automatically unlocks cursor)</li>
          </ul>
          <p>When the cursor is locked, you can look around freely. When it is unlocked, you can interact with UI elements like buttons and menus.</p>
        </>
      )
    },
    dailyPool: {
      title: `Daily ${rewardTokenSymbol} Pool`,
      content: (
        <>
          <p>The daily pool represents the total amount of {rewardTokenDisplay} rewarded to all eligible players combined.</p>
          <p>Currently, the pool distributes <strong>{formatTokenAmount(dailyTokenPool)} {rewardTokenSymbol}</strong> per day.</p>
          <p>The payout SQL job runs every <strong>{intervalMinutes} minutes</strong>, so rewards accrue across <strong>{intervalsPerDay}</strong> intervals each day.</p>
          <p>Your share depends on your Mining Power relative to the total Mining Power of all eligible players.</p>
        </>
      )
    },
    miningPower: {
      title: 'Mining Power',
      content: (
        <>
          <p>Mining Power determines your share of the daily {rewardTokenDisplay} pool. Higher power means a larger share each payout interval.</p>
          <p>Each rig tier provides a specific amount of Power:</p>
          <ul>
            <li>Tier 1: {getConstant(gameData, 'RIG_TIER_POWERS', [10])[0]} Power</li>
            <li>Tier 2: {getConstant(gameData, 'RIG_TIER_POWERS', [10, 15])[1]} Power</li>
            <li>Tier 3: {getConstant(gameData, 'RIG_TIER_POWERS', [10, 15, 25])[2]} Power</li>
            <li>Tier 4: {getConstant(gameData, 'RIG_TIER_POWERS', [10, 15, 25, 40])[3]} Power</li>
          </ul>
          <p>Backend formula per interval: <strong>(daily pool / {intervalsPerDay}) * your share of total mining power</strong>.</p>
          <p>To remain eligible, your connected wallet must keep at least <strong>{formatTokenAmount(minWalletBalance)} {miningRequirementTokenSymbol}</strong>.</p>
        </>
      )
    },
    energy: {
      title: 'Economy',
      content: (
        <>
          <p>New companies begin with <strong>{formatTokenAmount(startingBalance)} {rewardTokenSymbol}</strong>.</p>
          <p>Rig costs are <strong>{rigTierCosts.map((cost) => formatTokenAmount(cost)).join(' / ')} {rewardTokenSymbol}</strong>.</p>
          <p>Energy is still under development, so all rigs currently run with <strong>unlimited energy</strong>.</p>
          <p>Withdrawals use your in-game {rewardTokenSymbol} balance, while mining eligibility remains tied to holding REDDICKS in-wallet.</p>
        </>
      )
    }
  };

  return (
    <div className="board-menu-overlay" onClick={onClose}>
      <div className="board-container">
        <button className="close-button" onClick={onClose} aria-label="Close">x</button>
        <div className="board-menu" onClick={(e) => e.stopPropagation()}>
          <div className="board-layout">
            <nav className="board-nav">
              <ul>
                {Object.entries(sections).map(([key, { title }]) => (
                  <li key={key} className={activeSection === key ? 'active' : ''}>
                    <button onClick={() => setActiveSection(key)}>{title}</button>
                  </li>
                ))}
              </ul>
            </nav>
            <section className="board-content">
              <h2>{sections[activeSection].title}</h2>
              <div className="content-area">
                {sections[activeSection].content}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Board;
